import uuid from 'uuid/v4'
import fetch from '@jarvis.oddle/core/tools/fetch'
import { API_ROOT, origin, ODDLE_PASS_CLIENT_ID, ODDLE_PASS_REDIRECT_URL } from 'config/client'
import { stripSecureFlag, getCookie } from '@jarvis.oddle/core/tools/cookie'
import { makeQueryString, getCurrentUrl, dateToRequestString } from '@jarvis.oddle/core/tools/routes'
import {
  metaPath,
  userPath,
  menuPath,
  storeOrderPath,
  unavailableResourcePath,
  getAccessTokenPath,
  getOPAddressesPath
} from './common'
import {
  getDeliveryTrackingLinkPath,
  postStripeChargesPath,
  verifyAuthenticateTokenPath,
  getTermsAndConditionPath,
  getPrivacyPolicyPath,
  userInfoTarget,
  getAddressFormattingPath,
  updateDeliveryDetailsPath,
  getMenuFeaturesPath,
  getStoreOrderMetaDataPath,
  getStripeTokenPath,
  loginTarget,
  ocaLoginTarget,
  logoutTarget,
  userFacebookInfoTarget,
  updateUserTarget,
  updateUserPasswordTarget,
  resetPasswordTarget,
  updateDefaultUserAddressTarget,
  createUserAddressTarget,
  updateUserAddressTarget,
  removeUserAddressTarget,
  getUserOrderTarget,
  checkUserEmailTarget,
  verifyOTPTarget,
  resendOTPTarget,
  sendOTPTarget,
  createLoyaltyCustomerTarget,
  getCheckoutResultsTarget,
  updateOrderItemQuantityTarget
} from './server'
import { parseJwt } from 'tools/jwtParser'

const call = async (target, payload = true, options = { credentials: 'include' }, checkResult = true, autoParse = true, requiredToken = false) => {
  if (requiredToken && !(options.headers && options.headers['X-Oddle-Pass-Token'])) {
    return {}
  }
  const json = (await fetch(target, options, autoParse)) || {}
  // Handle error.
  if (checkResult) {
    if (json.errors) {
      // V4 shopping cart OMS API
      throw json.errors
    } else if (json.result === false || json.ok === false || (json.payload && json.payload.ok === false)) {
      // Old shopping cart OMS API
      throw (json.payload || {}).error || json.payload || {}
    }
  }

  // Process return data.
  if (payload) {
    if (typeof payload === 'function') {
      return payload(json)
    } else {
      return json.payload
    }
  } else {
    return json
  }
}

const retrieveCookies = (req) => typeof window !== 'undefined' ? document.cookie : req ? req.get ? req.get('Cookie') : req.headers.cookie : ''

const makeOptions = ({ payload, method = 'GET', include = true, clientId, req }) => {
  const enableOddlePass = getCookie(retrieveCookies(req), 'enableOddlePass') === 'true'
  let accessToken
  if (typeof window !== 'undefined' && window.localStorage.getItem('accessToken') && enableOddlePass) {
    const parsedToken = parseJwt(window.localStorage.getItem('accessToken'))
    if (parsedToken.exp >= new Date().getTime() / 1000) {
      accessToken = window.localStorage.getItem('accessToken')
    } else {
      window.localStorage.removeItem('accessToken')
    }
  }
  return ({
    credentials: include ? 'include' : '',
    method,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Cookie': req && req.get('Cookie'),
      'X-Request-Id': uuid(),
      ...accessToken ? {
        'X-Oddle-Pass-Token': accessToken,
        'X-Oddle-Pass-Client-Id': clientId || ODDLE_PASS_CLIENT_ID
      } : {}
    },
    body: JSON.stringify(payload)
  })
}

export const getShoppingCartMetaApi = async ({ name, storeId, referrer, req, res }) => {
  const response = await fetch(
    `${origin(req)}${API_ROOT}${metaPath}/${name}${makeQueryString({ storeId, referrer })}`,
    {
      credentials: 'include',
      headers: {
        ...((req && req.headers) || {}),
        // Custom header for OMS to identify the caller page URL
        // There is no standard header that fit our use case: https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers
        'Loaded-Url': getCurrentUrl(req, req.originalUrl)
      }
    },
    false
  )
  if (res) {
    const cookie = stripSecureFlag(response.headers._headers['set-cookie'])
    if (cookie) res.setHeader('set-cookie', cookie)

    const customUrl = req.get('Custom-Url')
    if (customUrl) {
      res.cookie('Custom-Url', customUrl)
    } else {
      res.clearCookie('Custom-Url')
    }
  }
  const json = await response.json()
  if (json.errors) {
    throw json.errors
  } else {
    return { meta: json.data }
  }
}

export const checkoutApi = ({ payload, req }) =>
  call(`${origin(req)}${API_ROOT}${storeOrderPath}/${payload.storeOrderId}/checkout`,
    (json) => ({ result: json.data }),
    makeOptions({ payload, method: 'POST' }),
    true
  )

export const getCheckoutMetaApi =
  ({ storeOrderId, req }) =>
    call(`${origin(req)}${API_ROOT}${storeOrderPath}/${storeOrderId}/checkout`,
      (json) => ({ meta: json.data }),
      makeOptions({ req }),
      true
    )

export const getCheckoutPaymentMethodsApi =
  ({ storeOrderId, req, type = 'all' }) => call(`${origin(req)}${API_ROOT}${storeOrderPath}/${storeOrderId}/payments/${type}`)

export const getInvoiceMetaApi =
  ({ storeOrderId, hashCode, req }) => {
    return (
      call(`${origin(req)}${API_ROOT}${storeOrderPath}/${storeOrderId}/invoice/${hashCode}`,
        (json) => ({ meta: json.data }),
        makeOptions({ req }),
        true
      )
    )
  }

export const checkUserExistenceApi = ({ menuId, userEmail, source, req }) => (
  call(`${origin(req)}${API_ROOT}${checkUserEmailTarget}${makeQueryString({ menuId, email: userEmail, source })}`, false)
)

export const facebookLoginApi =
  ({ cartName, storeOrderId, hash, req }) => call(`${origin(req)}${API_ROOT}${userFacebookInfoTarget}?cartName=${cartName}`,
    true,
    makeOptions({
      payload: { storeOrderId, hash },
      method: 'POST'
    })
  )

export const getUserApi = ({ cartName, clientId, req }) => call(
  `${origin(req)}${API_ROOT}${userPath}?subdomain=${cartName}`,
  true,
  makeOptions({
    method: 'GET',
    clientId
  })
)

export const createUserApi = ({ cartName, email, firstName, lastName, password, phone, storeOrderId, menuId, facebookId, source, gender, dob, hash, receiveEmail, acceptMarketing, acceptMarketingMenu, req }) => call(
  `${origin(req)}${API_ROOT}${userPath}?subdomain=${cartName}`,
  true,
  makeOptions({
    payload: { id: '', company: '', email, firstName, lastName, password, phone, menuId, storeOrderId, facebookId, hash, receiveEmail, source, gender, dob: dateToRequestString(dob), acceptMarketing, acceptMarketingMenu },
    method: 'POST'
  })
)

export const updateUserApi =
  ({ cartName, payload: { dob, ...rest }, req }) => call(
    `${origin(req)}${API_ROOT}${updateUserTarget}?subdomain=${cartName}`,
    true,
    makeOptions({
      payload: { dob: dateToRequestString(dob), ...rest },
      method: 'POST'
    })
  )

export const newsletterSignupApi = ({ email, firstName, lastName, menuId, req }) => call(
  `${origin(req)}${API_ROOT}${userPath}/newsletter/subscription`,
  () => ({ email }),
  makeOptions({
    payload: { email, firstName, lastName, menuId },
    method: 'POST'
  })
)

export const verifyOTPApi = ({ OTP, OTPSessionId, phone, menuId, req }) => call(
  `${origin(req)}${API_ROOT}${verifyOTPTarget}?menuId=${menuId}`,
  false,
  makeOptions({
    payload: {
      OTP,
      OTPSessionId,
      phone
    },
    method: 'POST'
  })
)

export const resendOTPApi = ({ OTPSessionId, menuId, req }) => call(
  `${origin(req)}${API_ROOT}${resendOTPTarget}?menuId=${menuId}`,
  false,
  makeOptions({
    payload: { OTPSessionId },
    method: 'POST'
  })
)

export const sendOTPApi = ({ purpose, menuId, phone, req }) => call(
  `${origin(req)}${API_ROOT}${sendOTPTarget}?menuId=${menuId}`,
  (json) => (json.data),
  makeOptions({
    payload: {
      purpose,
      phone
    },
    method: 'POST'
  })
)

export const createLoyaltyCustomer = ({ menuId, customerId, req }) => call(
  `${origin(req)}${API_ROOT}${createLoyaltyCustomerTarget}?menuId=${menuId}`,
  false,
  makeOptions({
    payload: { customerId },
    method: 'POST'
  })
)

export const getUserOrderApi =
  ({ cartName, customerId, menuId, orderId, req }) =>
    call(`${origin(req)}${API_ROOT}${getUserOrderTarget}`, true,
      makeOptions({
        payload: { customerId, orderId, subdomain: cartName, menuId },
        method: 'POST'
      }))

export const updateUserPasswordApi =
  ({ cartName, payload, req }) => call(
    `${origin(req)}${API_ROOT}${updateUserPasswordTarget}?subdomain=${cartName}`,
    true,
    makeOptions({ payload, method: 'POST' })
  )

export const userLoginApi =
  ({ cartName, email, password, source, storeOrderId, menuId, req }) => call(
    `${origin(req)}${API_ROOT}${loginTarget}?subdomain=${cartName}`,
    true,
    makeOptions({
      payload: { email, source, password, storeOrderId, menuId },
      method: 'POST'
    })
  )

// Fetch OCA user.
export const fetchOcaUserApi = ({ cartName, email, password, menuId, req }) => (
  call(
    `${origin(req)}${API_ROOT}${ocaLoginTarget}?subdomain=${cartName}`,
    (json) => json.data,
    makeOptions({
      payload: { email, password, menuId },
      method: 'POST'
    })
  )
)

export const userLogoutApi =
  ({ cartName, storeOrderId, req }) => call(
    `${origin(req)}${API_ROOT}${logoutTarget}?subdomain=${cartName}`,
    true,
    makeOptions({
      payload: { storeOrderId },
      method: 'POST'
    })
  )

export const userResetPasswordApi =
  ({ cartName, email, menuId, req }) => call(
    `${origin(req)}${API_ROOT}${resetPasswordTarget}?subdomain=${cartName}`,
    true,
    makeOptions({
      payload: {
        email,
        menuId
      },
      method: 'POST'
    })
  )

export const getAvailablePromotionsApi =
  ({ storeOrderId, req }) => call(`${origin(req)}${API_ROOT}${storeOrderPath}/${storeOrderId}/promotions/all`)

export const getEligiblePromotionsApi =
  ({ storeOrderId, req }) => call(`${origin(req)}${API_ROOT}${storeOrderPath}/${storeOrderId}/promotions`, true)

export const getPromoCodeApi =
  ({ storeOrderId, code, req }) => call(`${origin(req)}${API_ROOT}${storeOrderPath}/${storeOrderId}/promotions/${encodeURIComponent(code) || 'undefined'}`)

export const applyPromotionsApi =
  ({ storeOrderId, promotions, req }) => call(
    `${origin(req)}${API_ROOT}${storeOrderPath}/${storeOrderId}/promotions`,
    true,
    makeOptions({
      payload: { promotions },
      method: 'POST'
    })
  )

export const getPickupStoresApi =
  ({ storeOrderId, storeId, req }) => call(`${origin(req)}${API_ROOT}${storeOrderPath}/${storeOrderId}/stores${makeQueryString({ storeId })}`)

export const getAllStoresApi =
  ({ menuId, req }) => call(`${origin(req)}${API_ROOT}${menuPath}/${menuId}/stores`)

export const getTimeSlotsApi =
  ({ storeOrderId, date, req }) => call(`${origin(req)}${API_ROOT}${storeOrderPath}/${storeOrderId}/slots/${dateToRequestString(date)}`)

export const getStoreOrderApi =
  ({ storeOrderId, storeId, req }) => call(`${origin(req)}${API_ROOT}${storeOrderPath}/${storeOrderId}${makeQueryString({ storeId })}`)

export const updateStoreOrderItemQuantityApi =
  ({ storeOrderId, items, req }) => call(
    `${origin(req)}${API_ROOT}${updateOrderItemQuantityTarget}`,
    true,
    makeOptions({
      payload: { storeOrderId, items },
      method: 'PUT'
    })
  )

export const updateCollectionApi =
  ({ storeOrderId, method, storeId, req }) => call(
    `${origin(req)}${API_ROOT}${storeOrderPath}/${storeOrderId}/collection${makeQueryString({ storeId })}`,
    true,
    makeOptions({
      payload: { isPickup: method === 'pickup' },
      method: 'PUT'
    })
  )

export const updatePickupLocationApi =
  ({ storeOrderId, storeId, lat, long, selectedDate, req }) => call(
    `${origin(req)}${API_ROOT}${storeOrderPath}/${storeOrderId}/store`,
    true,
    makeOptions({
      payload: { storeId, lat, long, selectedDate },
      method: 'PUT'
    })
  )

export const createUserDeliveryAddressApi =
  ({ cartName, customerId, address, req }) => call(
    `${origin(req)}${API_ROOT}${createUserAddressTarget}`,
    true,
    makeOptions({
      payload: { address, customerId, cartName },
      method: 'POST'
    })
  )

export const updateUserDeliveryAddressApi =
  ({ customerId, address, req }) => call(
    `${origin(req)}${API_ROOT}${updateUserAddressTarget}`,
    true,
    makeOptions({
      payload: { address, customerId, addressId: address.id },
      method: 'POST'
    })
  )

export const removeUserDeliveryAddressApi =
  ({ customerId, addressId, req }) => call(
    `${origin(req)}${API_ROOT}${removeUserAddressTarget}`,
    () => ({ deletedAddressId: addressId }),
    makeOptions({
      payload: { addressId, customerId },
      method: 'POST'
    })
  )

export const setDefaultUserDeliveryAddressApi =
  ({ customerId, addressId, menuId, req }) => call(
    `${origin(req)}${API_ROOT}${updateDefaultUserAddressTarget}`,
    true,
    makeOptions({
      payload: { addressId, customerId, menuId },
      method: 'POST'
    })
  )

export const updateDeliveryLocationApi =
  ({ storeOrderId, address, storeId, req }) => call(
    `${origin(req)}${API_ROOT}${storeOrderPath}/${storeOrderId}/address${makeQueryString({ storeId })}`,
    true,
    makeOptions({
      payload: { storeOrderId, address },
      method: 'PUT'
    })
  )

export const updateDeliveryAddressTwoApi =
  ({ storeOrderId, addressTwo, req }) => call(
    `${origin(req)}${API_ROOT}${storeOrderPath}/${storeOrderId}/addressTwo`,
    (payload) => payload,
    makeOptions({
      payload: { id: storeOrderId, customerAddressTwo: addressTwo },
      method: 'PUT'
    })
  )

export const updateCollectionTimeApi =
  ({ storeOrderId, collectionDate, collectionTimeSlot, collectionTimeSlotId, req }) => call(
    `${origin(req)}${API_ROOT}${storeOrderPath}/${storeOrderId}/slots`,
    true,
    makeOptions({
      payload: {
        collectionDate: dateToRequestString(collectionDate),
        collectionTimeSlot,
        collectionTimeSlotId
      },
      method: 'PUT'
    })
  )

export const getMenuApi =
  ({ storeOrderId, menuId, req }) => call(`${origin(req)}${API_ROOT}/menu/${menuId}?storeOrderId=${storeOrderId}`)

export const addToCartApi =
  ({ storeOrderId, orderItem, menuItem, finalPrice, req }) => call(
    `${origin(req)}${API_ROOT}${storeOrderPath}/${storeOrderId}/items`,
    (json) => ({ ...json.payload, orderItem, menuItem, finalPrice }),
    makeOptions({ payload: orderItem, method: 'POST' })
  )

export const clearCartApi =
  ({ storeOrderId, req }) => call(
    `${origin(req)}${API_ROOT}${storeOrderPath}/${storeOrderId}/items`,
    true,
    makeOptions({ method: 'DELETE' })
  )

export const prepareCheckoutApi =
  ({ cartName, storeOrderId, req }) => call(
    `${origin(req)}${API_ROOT}${storeOrderPath}/${storeOrderId}/checkout/prepare?subdomain=${cartName}`,
    true
  )

export const getTermsAndConditionsApi = ({ menuId, req }) => call(
  `${origin(req)}${API_ROOT}${getTermsAndConditionPath(menuId)}`,
  (json) => json.data
)

export const getCheckoutResultsApi =
  ({ storeOrderId, req }) => call(
    `${origin(req)}${API_ROOT}${getCheckoutResultsTarget}?storeOrderId=${storeOrderId}`,
    true,
    { credentials: 'include' },
    true
  )

export const getTrackersApi =
  ({ menuId, req }) => call(
    `${origin(req)}${API_ROOT}${menuPath}/${menuId}/trackers`,
    false
  )

export const getScriptsApi =
  ({ menuId, req }) => call(
    `${origin(req)}${API_ROOT}${menuPath}/${menuId}/scripts`,
    (json) => json.payload.shoppingCartTrackers
  )

export const loadStoreItemAvailabilityApi = ({ menuId, req }) => call(
  `${origin(req)}${API_ROOT}${unavailableResourcePath}/${menuId}`,
  (json) => json.data
)

export const postStripeChargesApi = ({ payload, req }) => call(
  `${origin(req)}${API_ROOT}${postStripeChargesPath}`,
  (json) => json.data,
  makeOptions({ method: 'POST', payload })
)

export const verifyAuthenticateTokenApi = ({ storeOrderId, token, req }) => call(
  `${origin(req)}${API_ROOT}${verifyAuthenticateTokenPath(storeOrderId)}`,
  (json) => json.data,
  makeOptions({ method: 'POST', payload: { token } })
)

export const getDeliveryTrackingLinkApi = ({ storeOrderId, req }) => call(
  `${origin(req)}${API_ROOT}${getDeliveryTrackingLinkPath(storeOrderId)}`,
  (json) => json.data
)

export const getPrivacyPolicyApi = ({ menuId, req }) => call(
  `${origin(req)}${API_ROOT}${getPrivacyPolicyPath(menuId)}`,
  (json) => json.data
)

export const proxyApi = ({ req, url } = {}) => fetch(
  `${origin(req)}${API_ROOT}/proxy?url=${url}`
)

export const fetchUserViaOddlePassApi = ({ cartName, req }) => call(
  `${origin(req)}${API_ROOT}${userInfoTarget}?subdomain=${cartName}`,
  true,
  makeOptions({ method: 'GET' })
)

export const fetchMetaAddressFormatApi = ({ req, menuId }) => call(
  `${origin(req)}${API_ROOT}${getAddressFormattingPath(menuId)}`,
  json => json.data,
  makeOptions({ method: 'GET' })
)

export const updateDeliveryDetailsApi = ({ storeOrderId, deliveryAddressType, deliveryAddressTwoDetails, deliveryInstructions, req }) => call(
  `${origin(req)}${API_ROOT}${updateDeliveryDetailsPath(storeOrderId)}`,
  (json) => json.data,
  makeOptions({
    method: 'PUT',
    payload: { deliveryAddressType, deliveryAddressTwoDetails, deliveryInstructions }
  })
)

export const getMenuFeaturesApi = ({ req, menuId, features = [] }) => {
  const queryString = features.reduce((string, feature) => `${string}&features%5B%5D=${feature}`, '')
  return call(
    `${origin(req)}${API_ROOT}${getMenuFeaturesPath(menuId)}?${queryString}`,
    (json) => json.data,
    makeOptions({ method: 'GET' })
  )
}

export const getStoreOrderMetaDataApi = ({ storeOrderId, req }) =>
  call(`${origin(req)}${API_ROOT}${getStoreOrderMetaDataPath(storeOrderId)}`,
    (json) => ({ meta: json.data }),
    makeOptions({ req }),
    true,
    true,
    true
  )

export const getStripeTokenApi = ({ req, menuId, testAccount }) => call(
  `${origin(req)}${API_ROOT}/${getStripeTokenPath}${makeQueryString({ menuId, testAccount })}`,
  (json) => json.data,
  makeOptions({ method: 'GET' })
)

export const getOPAccessTokenApi = ({ req, customerId, menuId }) => call(
  `${origin(req)}${API_ROOT}${getAccessTokenPath}`,
  (json) => json.data,
  makeOptions({
    payload: {
      clientId: ODDLE_PASS_CLIENT_ID,
      redirectUri: ODDLE_PASS_REDIRECT_URL,
      menuId,
      customerId,
      state: { action: 're-new accessToken from Eats' }
    },
    method: 'POST'
  })
)

export const getOPAddressesApi = ({ req, customerId, eatsAccessToken }) => {
  let accessToken
  const enableOddlePass = getCookie(retrieveCookies(req), 'enableOddlePass') === 'true'
  if (typeof window !== 'undefined' && window.localStorage.getItem('accessToken') && enableOddlePass) {
    const parsedToken = parseJwt(window.localStorage.getItem('accessToken'))
    if (parsedToken.exp >= new Date().getTime() / 1000) {
      accessToken = window.localStorage.getItem('accessToken')
    } else {
      window.localStorage.removeItem('accessToken')
    }
  }
  return call(
    `${origin(req)}${API_ROOT}${getOPAddressesPath}${makeQueryString({ customerId })}`,
    (json) => json.data,
    ({
      credentials: 'include',
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Cookie': req && req.get('Cookie'),
        'X-Request-Id': uuid(),
        'X-Oddle-Pass-Token': eatsAccessToken || accessToken,
        'X-Oddle-Pass-Client-Id': ODDLE_PASS_CLIENT_ID
      },
    })
  )
}

export const updateCheckoutMetaApi = ({ req, payload, storeOrderId }) => {
  return call(
    `${origin(req)}${API_ROOT}${storeOrderPath}/${storeOrderId}`,
    (json) => json.data,
    makeOptions({ payload, method: 'PATCH' })
  )
}

